import type { Ref } from 'vue'
import { useMutation } from '/@/core/graphql'
import { gql } from '@apollo/client/core'
import type { NameInput, NoteInput, ProjectThingsInput } from '../types'
import type { RoomType } from '/@/common/rooms/types'
import { sectionFragment } from './fragments'
import { projectQuery } from './fragments'

interface SectionCreateInput {
  projectId: string
  name: string
  parentSectionId?: string
  roomType?: RoomType
  roomTypeExtra?: string
  shouldCreateDefaultShopList?: boolean
}

interface SectionDeleteInput {
  projectId: string
  sectionIds: string[]
}

interface SectionVisibilityInput {
  projectId: string
  sectionId: string
  isVisible: boolean
}

export function useSectionApi(projectId: Ref<string | undefined | null>) {
  const { mutate: create } = useMutation<{
    project: { section: { create: { recordId?: string } } }
  }>(
    gql`
      ${sectionFragment}
      mutation addSectionToProject($input: CreateSectionInput!) {
        project {
          section {
            create(input: $input) {
              recordId
              status
              project {
                id
                sections {
                  ...SectionProps
                }
              }
              record {
                id
                name
                note
                isVisible
                parentSectionId
              }
            }
          }
        }
      }
    `,
    {
      refetchQueries: () => {
        const id = projectId.value
        if (!id) return []
        return [
          {
            query: projectQuery,
            variables: { projectId: id },
          },
        ]
      },
      awaitRefetchQueries: true,
    },
  )

  const { mutate: remove } = useMutation(gql`
    ${sectionFragment}
    mutation removeSectionsFromProject($input: ProjectSectionsInput!) {
      project {
        section {
          delete(input: $input) {
            recordIds
            status
            project {
              id
              sections {
                ...SectionProps
              }
            }
          }
        }
      }
    }
  `)

  const { mutate: updateName } = useMutation(gql`
    mutation updateProjectSectionName($input: NameInput!) {
      project {
        section {
          updateName(input: $input) {
            recordId
            status
            record {
              id
              name
            }
          }
        }
      }
    }
  `)

  const { mutate: updateNote } = useMutation(gql`
    mutation updateProjectSectionNote($input: NoteInput!) {
      project {
        section {
          updateNote(input: $input) {
            recordId
            status
            record {
              id
              note
            }
          }
        }
      }
    }
  `)

  const { mutate: updateVisibility } = useMutation(gql`
    mutation updateProjectSectionVisibility($input: SectionVisibilityInput!) {
      project {
        section {
          updateVisibility(input: $input) {
            recordId
            status
            record {
              id
              isVisible
            }
          }
        }
      }
    }
  `)

  const { mutate: updateThingsOrdering } = useMutation(gql`
    mutation updateProjectThingsOrdering($input: ProjectThingsInput!) {
      project {
        section {
          updateThingsOrdering(input: $input) {
            status
            records {
              id
              ordering
            }
          }
        }
      }
    }
  `)

  return {
    async create(input: SectionCreateInput) {
      return await create({ input })
    },
    async remove(input: SectionDeleteInput) {
      await remove({ input })
    },
    async updateName(input: NameInput) {
      await updateName({ input })
    },
    async updateNote(input: NoteInput) {
      await updateNote({ input })
    },
    async updateVisibility(input: SectionVisibilityInput) {
      await updateVisibility({ input })
    },
    async updateThingsOrdering(input: ProjectThingsInput) {
      await updateThingsOrdering({ input })
    },
  }
}
